import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { isLocalDev } from '../../utils/isLocalDev';
import {
  CATEGORY_CHIPS,
  CITIES,
  GENRES,
  SEARCH_STAGES,
  TIME_CHIPS,
  filterEvents,
  followUps,
} from './discoveryMockData';
import '../../styles/mainPage.css';
import '../../styles/uiDiscoveryLab.css';

const STATUS_LABEL = {
  live: 'LIVE',
  soon: 'Starting soon',
  free: 'Free',
};

function Badge({ status }) {
  return <span className={`uilab-badge uilab-badge--${status}`}>{STATUS_LABEL[status]}</span>;
}

function EventCard({ event, rank, featured, onWatch, onRemind, reminded }) {
  return (
    <article className={`uilab-card${featured ? ' uilab-card--featured' : ''}`}>
      <div className="uilab-card-art" style={{ background: event.art }}>
        {rank ? <span className="uilab-rank">{rank}</span> : null}
        <Badge status={event.status} />
        <span className="uilab-heat">{event.heat.toLocaleString()} watching</span>
      </div>
      <div className="uilab-card-body">
        <h3>{event.title}</h3>
        <p>{event.meta} · {event.place}</p>
        <div className="uilab-card-actions">
          {event.status === 'live' ? (
            <button type="button" className="uilab-btn uilab-btn-primary" onClick={() => onWatch(event)}>
              Watch
            </button>
          ) : (
            <button
              type="button"
              className={`uilab-btn${reminded ? ' is-on' : ''}`}
              onClick={() => onRemind(event)}
            >
              {reminded ? 'Remind on' : 'Remind'}
            </button>
          )}
          <span className="uilab-reminds">{event.reminds} reminds</span>
        </div>
      </div>
    </article>
  );
}

const UiDiscoveryLab = () => {
  const navigate = useNavigate();
  const [city, setCity] = useState('Lisbon');
  const [time, setTime] = useState('tonight');
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [scene, setScene] = useState('browse');
  const [stage, setStage] = useState(0);
  const [reminded, setReminded] = useState({});
  const [hint, setHint] = useState('');
  const [picked, setPicked] = useState(null);

  const results = useMemo(
    () => filterEvents({
      city,
      time: scene === 'browse' ? time : null,
      category: scene === 'browse' ? category : 'all',
      query: scene === 'results' || scene === 'thin' ? query : '',
    }),
    [city, time, category, query, scene],
  );

  const browseResults = useMemo(
    () => filterEvents({ city, time, category, query: '' }),
    [city, time, category],
  );

  const isThin = scene === 'thin' || (scene === 'results' && results.length === 0);
  const railSource = isThin || scene === 'browse' ? browseResults : results;
  const numbered = railSource.slice(0, 8);
  const ranked = railSource.slice(0, 6);
  const chips = followUps({ city, time, query });

  useEffect(() => {
    if (scene !== 'searching') return undefined;
    setStage(0);
    const timers = [
      setTimeout(() => setStage(1), 700),
      setTimeout(() => setStage(2), 1400),
      setTimeout(() => {
        const next = filterEvents({ city, time: null, category: 'all', query });
        setScene(next.length ? 'results' : 'thin');
      }, 2100),
    ];
    return () => timers.forEach(clearTimeout);
  }, [scene, city, query]);

  if (!isLocalDev()) {
    return <Navigate to="/" replace />;
  }

  const runSearch = (value) => {
    const next = (value ?? query).trim();
    setQuery(next);
    setHint('');
    setPicked(null);
    if (!next) {
      setScene('browse');
      return;
    }
    setScene('searching');
  };

  const applyFollowUp = (chip) => {
    if (chip.startsWith('Tonight')) {
      setTime('tonight');
      setCategory('all');
      setQuery('');
      setScene('browse');
      return;
    }
    if (chip === 'Free events') {
      setCategory('free');
      setTime('tonight');
      setQuery('');
      setScene('browse');
      return;
    }
    if (chip === 'Live sports') {
      setCategory('sports');
      setTime('live');
      setQuery('');
      setScene('browse');
      return;
    }
    runSearch(query || 'Ibai');
  };

  const onRemind = (event) => {
    setReminded((prev) => ({ ...prev, [event.id]: !prev[event.id] }));
    setHint('Remind stays gated in product. This mock only toggles local state.');
  };

  const onWatch = (event) => {
    setPicked(event);
    setHint(`Watch would open ${event.meta}. Mock only — no stream.`);
  };

  return (
    <div className="uilab">
      <header className="app-header glass uilab-header">
        <div className="app-header-logo">
          <button type="button" className="logo-text-home" onClick={() => { setScene('browse'); setQuery(''); }}>
            LiveNow
          </button>
        </div>
        <nav className="app-header-nav">
          <span className="uilab-local">Local mock</span>
          <button type="button" className="livenow-login-btn" onClick={() => navigate('/')}>Real home</button>
        </nav>
      </header>

      <main className="uilab-main">
        <p className="uilab-banner">
          Prototype from Notion UI findings (14–15 Sep). Click chips, search, and cards.
          Map overlay, real EPG art, and production search routing are out of this mock.
        </p>

        <div className="uilab-discover">
          <button
            type="button"
            className="uilab-city"
            onClick={() => setCity((c) => (c === CITIES[0] ? CITIES[1] : CITIES[0]))}
          >
            Browsing in {city}
            <span>Change</span>
          </button>

          <form
            className="uilab-search"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch();
            }}
          >
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`What's live in ${city}?`}
              aria-label="Search live events"
            />
            <button type="submit" aria-label="Search">Search</button>
          </form>
        </div>

        <div className="uilab-chips" role="tablist" aria-label="When">
          {TIME_CHIPS.map((chip) => (
            <button
              key={chip.id}
              type="button"
              role="tab"
              aria-selected={time === chip.id}
              className={time === chip.id ? 'is-on' : ''}
              onClick={() => {
                setTime(chip.id);
                setScene('browse');
              }}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <div className="uilab-chips uilab-chips--cat" role="tablist" aria-label="Category">
          {CATEGORY_CHIPS.map((chip) => (
            <button
              key={chip.id}
              type="button"
              role="tab"
              aria-selected={category === chip.id}
              className={category === chip.id ? 'is-on' : ''}
              onClick={() => {
                setCategory(chip.id);
                setScene('browse');
              }}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {hint ? <p className="uilab-hint" role="status">{hint}</p> : null}

        {scene === 'searching' ? (
          <section className="uilab-status" aria-live="polite">
            <p className="uilab-query">Searching “{query}”</p>
            <ol>
              {SEARCH_STAGES.map((line, index) => (
                <li key={line} className={index <= stage ? 'is-on' : ''}>{line}</li>
              ))}
            </ol>
          </section>
        ) : null}

        {isThin && scene !== 'searching' ? (
          <section className="uilab-empty">
            <h2>No exact live match for “{query || 'this search'}”</h2>
            <p>Still on in {city}. Try “radiohead” to force this miss, or “ibai” / “porto” for hits.</p>
            <div className="uilab-chips">
              {chips.map((chip) => (
                <button key={chip} type="button" onClick={() => applyFollowUp(chip)}>{chip}</button>
              ))}
            </div>
          </section>
        ) : null}

        {scene !== 'searching' ? (
          <>
            <section className="uilab-section">
              <header className="uilab-section-head">
                <h2>{time === 'live' ? 'Happening now' : TIME_CHIPS.find((c) => c.id === time)?.label} in {city}</h2>
                <p>Numbered shortlist, not a runaway ticker.</p>
              </header>
              <div className="uilab-rail" tabIndex={0}>
                {numbered.length ? numbered.map((event, index) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    rank={index + 1}
                    featured
                    reminded={Boolean(reminded[event.id])}
                    onWatch={onWatch}
                    onRemind={onRemind}
                  />
                )) : (
                  <p className="uilab-muted">Nothing in this slice. Try another time or category chip.</p>
                )}
              </div>
            </section>

            <section className="uilab-section">
              <header className="uilab-section-head">
                <h2>Ranked in {city}</h2>
                <p>Heat is watching count plus reminds — not invented GDP stats.</p>
              </header>
              <ol className="uilab-rank-list">
                {ranked.map((event, index) => (
                  <li key={event.id}>
                    <span className="uilab-rank-index">{index + 1}</span>
                    <div className="uilab-rank-swatch" style={{ background: event.art }} />
                    <div>
                      <strong>{event.title}</strong>
                      <span>{event.meta} · {event.heat.toLocaleString()} watching · {event.reminds} reminds</span>
                    </div>
                    <Badge status={event.status} />
                    <button
                      type="button"
                      className="uilab-btn uilab-btn-ghost"
                      onClick={() => (event.status === 'live' ? onWatch(event) : onRemind(event))}
                    >
                      {event.status === 'live' ? 'Watch' : 'Remind'}
                    </button>
                  </li>
                ))}
              </ol>
            </section>

            <section className="uilab-section">
              <header className="uilab-section-head">
                <h2>Near you, by genre</h2>
                <p>Browse without knowing an artist name.</p>
              </header>
              {GENRES.map((genre) => {
                const items = filterEvents({ city, time: null, category: 'all', query: '' })
                  .filter((event) => event.genre === genre)
                  .slice(0, 4);
                if (!items.length) return null;
                return (
                  <div key={genre} className="uilab-genre">
                    <h3>{genre}</h3>
                    <div className="uilab-rail uilab-rail--compact">
                      {items.map((event) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          reminded={Boolean(reminded[event.id])}
                          onWatch={onWatch}
                          onRemind={onRemind}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </section>
          </>
        ) : null}

        {picked ? (
          <p className="uilab-picked">Selected: {picked.title}</p>
        ) : null}

        <p className="uilab-skip">
          Skipped on purpose: Google Maps overlay, deep-link `/#/search/...`, production how-it-works copy, and real programme thumbnails.
        </p>
      </main>
    </div>
  );
};

export default UiDiscoveryLab;
